import type { SocialCaptureResponse } from "contracts/api/social/admin";
import { SOCIAL_MEDIA_KIND } from "contracts/enums/social";
import { SOCIAL_STANDARD_LABELS } from "contracts/enums/social-labels";
import { For, Show } from "solid-js";
import {
  POST_ANALYSIS_STATE,
  POST_ANALYSIS_STATE_LABELS,
  REPORT_JUDGMENT_LABELS,
  REPORT_SEVERITY_LABELS,
} from "../../enums/social-report";
import { formatDate } from "../../ui/format";
import { SocialReportEvidence } from "./SocialReportEvidence";
import { postAnalysisState } from "./socialReportModel";

/** One captured post: media, caption, engagement, and its latest analysis. */
export function SocialReportPostCard(props: { post: SocialCaptureResponse }) {
  const state = () => postAnalysisState(props.post);
  return (
    <article class="social-report-post" data-analysis-state={state()}>
      <header class="social-report-post-head">
        <div>
          <strong>@{props.post.authorHandle}</strong>
          <span class="td-muted">
            {" "}
            {formatDate(new Date(props.post.publishedAt))}
          </span>
        </div>
        <a href={props.post.canonicalUrl} target="_blank" rel="noreferrer">
          Open post
        </a>
      </header>
      <Show when={props.post.assets.length > 0}>
        <div class="social-media-grid">
          <For each={props.post.assets}>
            {(asset) => <PostMedia asset={asset} />}
          </For>
        </div>
      </Show>
      <Show when={props.post.text} keyed>
        {(text) => <p class="social-post-text">{text}</p>}
      </Show>
      <div class="social-run-facts mono">
        <span>{engagement("likes", props.post.engagement.likes)}</span>
        <span>{engagement("comments", props.post.engagement.comments)}</span>
        <span>{engagement("shares", props.post.engagement.shares)}</span>
        <span>{engagement("views", props.post.engagement.views)}</span>
      </div>
      <PostAnalysis post={props.post} state={state()} />
    </article>
  );
}

function PostAnalysis(props: {
  post: SocialCaptureResponse;
  state: ReturnType<typeof postAnalysisState>;
}) {
  return (
    <section class="social-report-analysis">
      <div class="social-report-analysis-head">
        <strong>{POST_ANALYSIS_STATE_LABELS[props.state]}</strong>
      </div>
      <Show when={props.state === POST_ANALYSIS_STATE.FAILED}>
        <p class="td-muted">
          The automated analysis of this post did not finish. The captured post
          and its evidence remain available.
        </p>
      </Show>
      <Show when={props.post.analysis} keyed>
        {(analysis) => (
          <>
            <Show when={analysis.summary} keyed>
              {(summary) => <p>{summary}</p>}
            </Show>
            <div class="social-judgments">
              <For each={analysis.judgments}>
                {(judgment) => (
                  <div
                    class="social-report-judgment"
                    data-judgment={judgment.judgment}
                  >
                    <strong>{SOCIAL_STANDARD_LABELS[judgment.standard]}</strong>
                    <span>{REPORT_JUDGMENT_LABELS[judgment.judgment]}</span>
                    <p>{judgment.rationale}</p>
                    <SocialReportEvidence
                      keys={judgment.evidenceKeys}
                      catalog={analysis.evidence}
                    />
                  </div>
                )}
              </For>
            </div>
            <For each={analysis.findings}>
              {(finding) => (
                <div class="social-report-finding" data-sev={finding.severity}>
                  <strong>{finding.title}</strong>
                  <span class={`badge badge-sev sev-${finding.severity}`}>
                    {REPORT_SEVERITY_LABELS[finding.severity]}
                  </span>
                  <p>{finding.detail}</p>
                  <p>
                    <b>Recommended action:</b> {finding.recommendedAction}
                  </p>
                  <SocialReportEvidence
                    keys={finding.evidenceKeys}
                    catalog={analysis.evidence}
                  />
                </div>
              )}
            </For>
            <Show when={analysis.evidence.length > 0}>
              <details class="social-evidence">
                <summary>
                  Everything the analysis was allowed to look at
                </summary>
                <For each={analysis.evidence}>
                  {(evidence) => (
                    <div class="social-evidence-row">
                      <code>{evidence.key}</code>
                      <span>{evidence.description}</span>
                      <Show when={evidence.excerpt} keyed>
                        {(excerpt) => <q>{excerpt}</q>}
                      </Show>
                    </div>
                  )}
                </For>
              </details>
            </Show>
          </>
        )}
      </Show>
    </section>
  );
}

function PostMedia(props: { asset: SocialCaptureResponse["assets"][number] }) {
  if (props.asset.kind === SOCIAL_MEDIA_KIND.VIDEO) {
    return <video controls muted preload="metadata" src={props.asset.url} />;
  }
  if (props.asset.kind === SOCIAL_MEDIA_KIND.AUDIO) {
    return (
      <a
        class="social-audio-link"
        href={props.asset.url}
        target="_blank"
        rel="noreferrer"
      >
        Open captured audio
      </a>
    );
  }
  return (
    <img
      src={props.asset.url}
      alt={props.asset.altText ?? "Captured post media"}
    />
  );
}

function engagement(label: string, value: number | null): string {
  return `${value ?? "—"} ${label}`;
}
